/**
 * vue-query 클라이언트 — 단일 인스턴스 SSoT.
 *
 * main.ts(엔트리)가 아닌 독립 모듈에 둔다 — 컴포넌트 밖(스토어·인터셉터)에서 정적 import 로 캐시를
 * 직접 정리할 수 있게 하면서 엔트리와의 순환 의존(main→router→store→main)을 피한다.
 * 특히 로그아웃 시 사용자별 캐시(예: 보유 grant)를 비워 다음 사용자에게 누출되지 않도록 한다.
 */
import { QueryClient } from '@tanstack/vue-query';

/**
 * QueryClient 생성기 — 기본 옵션 SSoT. 브라우저는 아래 단일 인스턴스를 쓰고, 빌드 프리렌더는 라우트마다 새로
 * 만든다(main.ts) — vite-ssg 가 라우트를 병렬 렌더하므로 공유 인스턴스면 다른 페이지 데이터가 HTML 에 섞인다.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

export const queryClient = createQueryClient();

/**
 * 보유 권한(download_grant) 변동 시 무효화할 파생 쿼리 키 — <b>SSoT</b>.
 *
 * 수령·결제·환불처럼 grant 가 생기거나 회수되는 지점은 반드시 {@link invalidateGrantDerivedQueries}
 * 를 호출한다. 화면마다 아는 키만 골라 무효화하면(과거: 수령 0건·환불은 분석 키 누락) 전역
 * `staleTime`(60s) 동안 다른 화면이 stale 캐시를 그대로 써서 "방금 받았는데 아직 잠김" 같은
 * 불일치가 난다. grant 를 소비하는 화면이 늘면 <b>이 목록에만</b> 키를 추가한다.
 */
const GRANT_DERIVED_QUERY_KEYS: readonly (readonly string[])[] = [
  ['fo', 'my-grants'], // 자료 탐색 보유 판정(중복 담기 방지)
  ['mypage', 'library'], // 자료실 목록
  ['catalog', 'ownership'], // 상품군 보유·확정 대기(선택 불가 판정, 079)
  ['fo', 'free-claim-quota'], // 무료 수령 잔여(수령·환불로 변동)
  ['fo', 'analysis', 'access-status'], // 정시 분석 열람 현황(038)
  ['analysis', 'jeongsi', 'departments'], // 목록 access 배지
  ['analysis', 'jeongsi', 'detail'], // 상세 access·잠금 분기
  ['analysis', 'jeongsi', 'interpretation'], // 해제 후 해석 재조회
  ['analysis', 'susi', 'universities'], // 수시 대학 목록 보유 배지(040)
  ['analysis', 'susi', 'tracks'], // 수시 access·층③ 잠금 분기(040)
  ['analysis', 'susi', 'interpretation'], // 수시 해제 후 준비 방향 재조회(040)
];

/** 보유 권한 변동(수령·결제·환불) 후 파생 화면 캐시를 일괄 무효화한다. */
export function invalidateGrantDerivedQueries(): void {
  for (const key of GRANT_DERIVED_QUERY_KEYS) {
    void queryClient.invalidateQueries({ queryKey: [...key] });
  }
}
